import { useCallback, useEffect, useRef, useState } from "react";

export function useTableGrabber() {
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [tableOverflowing, setTableOverflowing] = useState(false);

  const isMouseDownRef = useRef(false);
  const isDraggingRef = useRef(false);
  const scrollerRef = useRef<Element | null>(null);
  const startPosRef = useRef({ x: 0, y: 0 });

  const DRAG_THRESHOLD = 5;

  const setRef = useCallback((node) => {
    if (node) tableRef.current = node;
  }, []);

  useEffect(() => {
    if (!tableRef.current) return;

    scrollerRef.current = tableRef.current.getElementsByClassName("MuiDataGrid-virtualScroller")[0];

    if (scrollerRef.current === null) return;

    const resizeObserver = new ResizeObserver((event) => {
      if (!scrollerRef.current) return;
      if (scrollerRef.current.scrollWidth > scrollerRef.current.clientWidth) {
        setTableOverflowing(true);
      } else setTableOverflowing(false);
    });

    if (scrollerRef && scrollerRef.current) resizeObserver.observe(scrollerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [tableRef.current]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const contentEl = el.getElementsByClassName("MuiDataGrid-virtualScrollerContent ")[0];

    if (!contentEl) return;

    if (tableOverflowing) {
      el.classList && el.classList.add("draggable");
      contentEl.addEventListener("mousedown", handleMouseDown);
      window.addEventListener("mouseup", handleMouseUp);
      contentEl.addEventListener("mousemove", handleMouseMove);
    } else {
      el.classList && el.classList.remove("draggable");
      contentEl.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      contentEl.removeEventListener("mousemove", handleMouseMove);
    }

    return () => {
      if (!el) return;
      contentEl.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      contentEl.removeEventListener("mousemove", handleMouseMove);
    };
  }, [scrollerRef.current, tableOverflowing]);

  function captureClick(e) {
    e.stopPropagation(); // Stop the click from being propagated.

    window.removeEventListener("click", captureClick, true); // cleanup
  }

  const handleMouseMove = useCallback((e) => {
    if (!scrollerRef.current || !isMouseDownRef.current) return;

    if (!isDraggingRef.current) {
      const dx = e.clientX - startPosRef.current.x;
      const dy = e.clientY - startPosRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > DRAG_THRESHOLD) {
        isDraggingRef.current = true;
        scrollerRef.current.classList.add("dragging");
        // Add a click listener to stop the click event from being propagated, used to not trigger row selection or panel opening.
        window.addEventListener("click", captureClick, true);
      }
    }

    if (isDraggingRef.current) scrollerRef.current!.scrollLeft -= e.movementX;
  }, []);

  const handleMouseDown = useCallback((e) => {
    isMouseDownRef.current = true;
    startPosRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseUp = useCallback(() => {
    isMouseDownRef.current = false;
    isDraggingRef.current = false;
    if (scrollerRef.current) {
      scrollerRef.current.classList.remove("dragging");
    }
  }, []);

  return setRef;
}
